import Route from '@ember/routing/route';
import { action } from '@ember/object';
import RSVP from 'rsvp';
import { service } from '@ember/service';
import { STATUS_SNAPSHOT_MAX_AGE_MS } from 'garaje/services/device-status-service';
import { isListableIpadStatus } from 'garaje/utils/device-type';
import { IPAD_LAST_SEEN_THRESHOLD } from 'garaje/utils/decorators/device-status';

const SORT_COL_TO_FIELD_NAME = {
  'iPad type': 'deviceType',
  Location: 'location.name',
  'Wi-Fi network': 'wifiNetwork',
  'iOS version': 'osVersion',
  'Visitors version': 'appVersion',
};

const ORDER_DESC_VALS = ['Z-A', 'Oldest to newest'];

const PAGE_SIZE = 25;

// Caps how many locations' device-status scopes are fetched concurrently, so a
// global admin readable at hundreds of locations doesn't fire hundreds of
// concurrent snapshot queries on one page load.
const SCOPE_FETCH_CONCURRENCY = 8;

function telemetryValue(status, key) {
  return status.telemetry?.[key] ?? '';
}

function sortFieldValue(status, field, locationNameById) {
  switch (field) {
    case 'deviceType':
      return telemetryValue(status, 'device-type');
    case 'wifiNetwork':
      return telemetryValue(status, 'wifi-network');
    case 'osVersion':
      return telemetryValue(status, 'os-version');
    case 'appVersion':
      return telemetryValue(status, 'app-version');
    case 'location.name':
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
      return locationNameById.get(String(status.locationId)) ?? '';
    default:
      return '';
  }
}

export default class LocationOverviewGlobalDevicesIpadsRoute extends Route {
  @service abilities;
  @service('device-status-service') deviceStatusService;
  @service flashMessages;
  @service locationFeatureFlags;
  @service router;
  @service skinnyLocations;
  @service store;

  queryParams = {
    sortBy: {
      refreshModel: true,
    },
    sortDirection: {
      refreshModel: true,
    },
    sortOrder: {
      refreshModel: true,
    },
    locationId: {
      refreshModel: true,
    },
    wifiNetwork: {
      refreshModel: true,
    },
    deviceType: {
      refreshModel: true,
    },
    pageNumber: {
      refreshModel: true,
    },
    status: {
      refreshModel: true,
    },
  };

  beforeModel() {
    if (!this.abilities.can('read global-devices')) {
      this.router.transitionTo('unauthorized');
    }
  }

  async model({ sortBy, sortDirection, locationId, wifiNetwork, deviceType, status, pageNumber }) {
    if (this.locationFeatureFlags.isEnabled('devices-service-status-api')) {
      const iPads = await this.loadIpadsFromDeviceStatusService({
        sortBy,
        sortDirection,
        locationId,
        wifiNetwork,
        deviceType,
        status,
        pageNumber,
      });

      return RSVP.hash({ iPads });
    }

    const params = { filter: {} };
    const iPadThresholdSeconds = Math.floor(IPAD_LAST_SEEN_THRESHOLD / 1000);

    if (locationId) {
      params.filter.location = locationId;
    } else {
      const locations = this.skinnyLocations.readableByCurrentAdmin;
      params.filter.location = locations.mapBy('id').join();
    }

    if (status === 'operational') {
      params.filter['heartbeat-seconds-ago'] = `>${iPadThresholdSeconds}`;
    }

    if (status === 'offline') {
      params.filter['heartbeat-seconds-ago'] = `<${iPadThresholdSeconds}`;
    }

    if (wifiNetwork) {
      params.filter['wifi-network'] = wifiNetwork;
    }

    if (deviceType) {
      // This query param has to be wrapped an array.
      // Given values like `iPad4,5` and `iPad5,3`, the backend
      // will interpret as an array `['iPad4', '5']` without the quotes
      params.filter['device-type'] = [deviceType];
    }

    const orderDescending = ORDER_DESC_VALS.includes(sortDirection);
    const sortOrder = orderDescending ? '-' : '';
    const sortProp = SORT_COL_TO_FIELD_NAME[sortBy];

    if (sortBy) {
      params.sort = `${sortOrder}${sortProp}`;
    }

    const offset = PAGE_SIZE * pageNumber - PAGE_SIZE;

    params.page = {
      offset,
      limit: PAGE_SIZE,
    };

    params.include = 'location,printer';

    const iPads = await this.store.query('device', params);

    return RSVP.hash({
      iPads,
    });
  }

  // Sources rows from devices-service snapshots instead of the envoy-web devices
  // endpoint, so the status shown and the status filtered/sorted on are always the
  // same value (unlike the legacy path, whose heartbeat-based filter can disagree
  // with the devices-service-driven status rendered on screen).
  async loadIpadsFromDeviceStatusService({
    sortBy,
    sortDirection,
    locationId,
    wifiNetwork,
    deviceType,
    status,
    pageNumber,
  }) {
    const targetLocationIds = (locationId ? [locationId] : this.skinnyLocations.readableByCurrentAdmin.mapBy('id')).map(
      String,
    );

    const { failedLocationIds, truncatedLocationIds } = await this.#ensureScopeSnapshots(targetLocationIds);

    const scopedStatuses = this.#scopedStatuses(targetLocationIds);
    const filteredStatuses = this.#filterStatuses(scopedStatuses, { status, wifiNetwork, deviceType });
    this.#sortStatuses(filteredStatuses, sortBy, sortDirection);

    const offset = PAGE_SIZE * pageNumber - PAGE_SIZE;
    const pageStatuses = filteredStatuses.slice(offset, offset + PAGE_SIZE);
    const iPads = await this.#buildDeviceRecords(pageStatuses);

    iPads.meta = {
      total: filteredStatuses.length,
      'wifi-networks': [...new Set(scopedStatuses.map((s) => telemetryValue(s, 'wifi-network')).filter(Boolean))],
      'device-types': [...new Set(scopedStatuses.map((s) => telemetryValue(s, 'device-type')).filter(Boolean))],
    };
    if (failedLocationIds.length || truncatedLocationIds.length) {
      iPads.meta.partial = { failedLocationIds, truncatedLocationIds };
      this.flashMessages.showFlash(
        'warning',
        'Device statuses for some locations could not be fully loaded. Results may be incomplete.',
      );
    }

    return iPads;
  }

  // Loads a fresh-enough snapshot for every target location and reports which
  // locations failed or are truncated. These locations have no MQTT coverage (only
  // the current location's topic is subscribed), so snapshot age is capped —
  // otherwise a scope cached on a previous visit would serve its first-ever
  // snapshot forever. A bounded worker pool starts the next location as soon as
  // any worker finishes, avoiding batch barriers while still limiting pressure on
  // devices-service.
  async #ensureScopeSnapshots(targetLocationIds) {
    const scopeResults = new Array(targetLocationIds.length);
    let nextScopeIndex = 0;
    const fetchNextScope = async () => {
      while (nextScopeIndex < targetLocationIds.length) {
        const index = nextScopeIndex;
        nextScopeIndex += 1;
        scopeResults[index] = await this.deviceStatusService.ensureStatuses(
          targetLocationIds[index],
          'visitors',
          'location',
          { maxAgeMs: STATUS_SNAPSHOT_MAX_AGE_MS },
        );
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(SCOPE_FETCH_CONCURRENCY, targetLocationIds.length) }, fetchNextScope),
    );

    return {
      failedLocationIds: targetLocationIds.filter((_id, index) => !scopeResults[index]),
      truncatedLocationIds: targetLocationIds.filter((id) =>
        this.deviceStatusService.truncatedScopeKeys.has(`location:${id}`),
      ),
    };
  }

  #scopedStatuses(targetLocationIds) {
    const targetLocationIdSet = new Set(targetLocationIds);
    const showSimulators = this.locationFeatureFlags.isEnabled('visitors-devices-dashboard-show-simulators');
    return Array.from(this.deviceStatusService.statusByDeviceId.values()).filter(
      (s) =>
        s.product === 'visitors' &&
        targetLocationIdSet.has(String(s.locationId)) &&
        isListableIpadStatus(s, { showSimulators }),
    );
  }

  #filterStatuses(statuses, { status, wifiNetwork, deviceType }) {
    return statuses.filter((s) => {
      if (status === 'operational' && s.connectionStatus !== 'CONNECTED') {
        return false;
      }
      if (status === 'offline' && s.connectionStatus === 'CONNECTED') {
        return false;
      }
      if (wifiNetwork && telemetryValue(s, 'wifi-network') !== wifiNetwork) {
        return false;
      }
      if (deviceType && telemetryValue(s, 'device-type') !== deviceType) {
        return false;
      }
      return true;
    });
  }

  // In-place sort matching the legacy server sort columns; defaults to device name.
  #sortStatuses(statuses, sortBy, sortDirection) {
    const locationNameById = this.#locationNameById();
    const orderDescending = ORDER_DESC_VALS.includes(sortDirection);
    const sortField = SORT_COL_TO_FIELD_NAME[sortBy];
    statuses.sort((a, b) => {
      const aVal = sortBy ? sortFieldValue(a, sortField, locationNameById) : telemetryValue(a, 'name');
      const bVal = sortBy ? sortFieldValue(b, sortField, locationNameById) : telemetryValue(b, 'name');
      const result = String(aVal).localeCompare(String(bVal));
      return orderDescending ? -result : result;
    });
  }

  #locationNameById() {
    return new Map(this.skinnyLocations.readableByCurrentAdmin.map((location) => [String(location.id), location.name]));
  }

  // Turns the page's statuses into store-backed DeviceModel records: pushes
  // location name stubs (so `ipad.location.name` renders — skinny-locations are a
  // different model type), hydrates printer relationships, then pushes telemetry.
  // Printers must load BEFORE the telemetry push: the printer sideload hydrates a
  // stub device's `printer` relationship, and the telemetry push overwrites that
  // stub's attributes without touching relationships — building the devices first
  // would let the telemetry push race the sideload.
  async #buildDeviceRecords(pageStatuses) {
    const pageLocationIds = [...new Set(pageStatuses.map((s) => String(s.locationId)).filter(Boolean))];
    const locationNameById = this.#locationNameById();

    for (const id of pageLocationIds) {
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
      const name = locationNameById.get(id);
      // Raw store.push bypasses serializer normalization, so the type must be the
      // singular model name (unlike the 'locations'/'devices' types inside the
      // store.normalize payload below, which the serializer singularizes).
      this.store.push({
        data: { type: 'location', id, attributes: { name } },
      });
    }

    await Promise.all(
      pageLocationIds.map((id) => this.store.query('printer', { filter: { location: id }, include: 'devices' })),
    );

    return pageStatuses.map((s) => {
      const normalized = this.store.normalize('device', {
        type: 'devices',
        id: s.externalId,
        attributes: {
          ...s.telemetry,
          'is-sleeping': s.telemetry?.['sleeping'] ?? false,
          'is-connected': s.isConnected,
          // connectionStatusUpdatedAt is a Date only when the record came through
          // serializer normalization — raw store.push paths (MQTT-created partials)
          // leave the ISO string as-is, since @attr transforms don't run on push.
          'last-heartbeat-at': s.connectionStatusUpdatedAt ? new Date(s.connectionStatusUpdatedAt).toISOString() : null,
        },
        relationships: {
          location: { data: { type: 'locations', id: String(s.locationId) } },
        },
      });
      return this.store.push(normalized);
    });
  }

  @action
  refreshModel() {
    this.refresh();
  }
}
